import axios from 'axios'
import { Store } from 'react-notifications-component'
import { AuthService } from '../services'
import { APIPath, CommonConstant, ImpersonationErrorCode } from '../constant'
import { ImpersonationUtility } from '../impersonation-utility'
import { getRecaptchaToken } from '../recaptcha'
import { isRoleRefusal, roleRefusalMessage } from '../role-model'
import {
    emitPlanLimitBlocked,
    getPlanLimitCode,
    isPlanLimitErrorCode,
} from '../plan-limit-events'
import {
    getAuditAccessErrorCode,
    isAuditAccessErrorCode,
    parseAuditAccessError,
    shouldSuppressClaimOwnerLockToast,
} from '../audit-access'

const onSuccess = response => response.data

// Guards against re-entrancy: the cleanup below fires more requests (best-effort
// ImpersonateEnd) which can 401 again before we redirect away.
let impersonationExpiryHandled = false

// Screen 4 of the handoff: any authenticated request that comes back with
// 401 + ImpersonationExpired (idle/hard-cap) drops the elevated session, swaps
// the stashed admin token back in, and bounces to the admin view.
const handleImpersonationExpired = () => {
    if (impersonationExpiryHandled) return
    impersonationExpiryHandled = true

    // Best-effort server cleanup; we don't wait for it since we redirect.
    axios
        .create({ baseURL: APIPath.server })
        .post(
            APIPath.impersonateEnd,
            { LoginSource: 1 },
            { headers: { 'X-TOKEN': AuthService.getToken(), app: 'EAuditor' } },
        )
        .catch(() => {})

    ImpersonationUtility.restoreAdmin(null)
    Store.addNotification({
        message: 'Impersonation session ended',
        type: 'warning',
        insert: 'top',
        container: 'top-right',
        dismiss: { duration: 4000 },
    })
    window.location.assign('/app/settings/menu-permission?permissionTab=user')
}

const onError = async error => {
    const status = error?.response?.status
    const data = error?.response?.data
    const code = getPlanLimitCode(data) || data?.ErrorCode || data?.errorCode
    if (
        status === 401 &&
        code === ImpersonationErrorCode.ImpersonationExpired &&
        ImpersonationUtility.isImpersonating()
    ) {
        handleImpersonationExpired()
    }
    // Free-plan seat/template cap (EAUDITOR-692). Opens global block dialog.
    if (status === 403 && isPlanLimitErrorCode(code)) {
        emitPlanLimitBlocked({
            ...(typeof data === 'object' && data ? data : {}),
            code,
            message: data?.message || data?.Message || data?.ErrorMessage,
        })
    }
    // CrossUserAccess: owner-only / post-complete lock on write endpoints.
    // Toast once here so every gated POST/PUT/DELETE shares the same UX.
    const auditAccessCode = getAuditAccessErrorCode(data) || code
    if (status === 403 && isAuditAccessErrorCode(auditAccessCode)) {
        const responseBody =
            typeof data === 'object' && data ? data : { code: auditAccessCode }
        const skipClaimOwnerToast = shouldSuppressClaimOwnerLockToast(
            error?.config?.data,
            responseBody,
            auditAccessCode,
        )
        const parsed = parseAuditAccessError({
            status,
            error: responseBody,
        })
        if (parsed.message && !skipClaimOwnerToast) {
            Store.addNotification({
                message: parsed.message,
                type: 'danger',
                insert: 'top',
                container: 'top-right',
                dismiss: { duration: 5000 },
            })
        }
    }
    // Department role model: the server refused the action for the user's role (opted-in companies only).
    const roleRefused = isRoleRefusal(status, data)
    if (roleRefused) {
        Store.addNotification({
            message: roleRefusalMessage(data),
            type: 'danger',
            insert: 'top',
            container: 'top-right',
            dismiss: { duration: 5000 },
        })
    }
    return Promise.reject({
        error: data?.error || data,
        status,
        roleRefusalHandled: roleRefused,
        // Call sites can skip a second toast when this is set.
        auditAccessHandled: status === 403 && isAuditAccessErrorCode(auditAccessCode),
    })
}

const request = async (options, isSecure, recaptchaToken = null) => {
    const headers = {}

    if (isSecure) {
        const token = AuthService.getToken()
        headers['X-TOKEN'] = `${token}`
    }

    // Always include reCAPTCHA header; empty string if token not available
    headers['X-Recaptcha'] = recaptchaToken || ''
    if (process.env.NODE_ENV !== 'production') {
        // For client verification only; do not enable in production
        // eslint-disable-next-line no-console
        console.log('[reCAPTCHA] X-Recaptcha header token:', recaptchaToken || '(empty)')
    }

    headers.app = 'EAuditor'
    headers['Access-Control-Allow-Origin'] = '*'
    headers['X-LoginSource'] = '1'

    const client = axios.create({
        baseURL: APIPath.server,
        headers: { ...headers },
    })

    return client(options).then(onSuccess).catch(onError)
}

const uploadFiles = (url, data, headers = {}) => {
    const token = AuthService.getToken()
    headers['X-TOKEN'] = `${token}`
    headers.app = 'EAuditor'
    headers['Access-Control-Allow-Origin'] = '*'
    headers['X-LoginSource'] = '1'
    const client = axios({
        url,
        method: 'POST',
        headers: {
            ...headers,
        },
        data,
    })

    return client.then(onSuccess).catch(onError)
}

export class BaseService {
    static get(url, isSecure = true, recaptchaToken = null) {
        return request(
            {
                url,
                method: 'GET',
            },
            isSecure,
            recaptchaToken,
        )
    }

    static async post(url, data, isSecure = true, recaptchaToken = null) {
        let payload
        if (Array.isArray(data)) {
            // If data is an array, add LoginSource inside each object
            payload = data.map(item => ({
                ...item,
                LoginSource: 1,
            }))
        } else if (typeof data === 'object' && data !== null) {
            // If data is an object, add LoginSource at the root
            payload = { ...data, LoginSource: 1 }
        } else {
            // For other types, just send as is
            payload = data
        }
        const token = recaptchaToken ?? (await getRecaptchaToken('post'))
        return request(
            {
                url,
                method: 'POST',
                data: payload,
            },
            isSecure,
            token,
        )
    }

    // POST with URI/query params only — no request body (ASP.NET Help: Body Parameters None)
    static async postNoBody(url, isSecure = true, recaptchaToken = null) {
        const token = recaptchaToken ?? (await getRecaptchaToken('post'))
        return request(
            {
                url,
                method: 'POST',
                transformRequest: [
                    (data, headers) => {
                        if (headers) {
                            delete headers['Content-Type']
                            delete headers['content-type']
                        }
                        return data
                    },
                ],
            },
            isSecure,
            token,
        )
    }

    // Send POST without mutating payload (no LoginSource injected into body)
    static async postExact(url, data, isSecure = true, recaptchaToken = null) {
        const token = recaptchaToken ?? (await getRecaptchaToken('post'))
        return request(
            {
                url,
                method: 'POST',
                data,
            },
            isSecure,
            token,
        )
    }

    // Send PUT without mutating payload (no LoginSource injected into body)
    static async putExact(url, data, isSecure = true, recaptchaToken = null) {
        const token = recaptchaToken ?? (await getRecaptchaToken('put'))
        return request(
            {
                url,
                method: 'PUT',
                data,
            },
            isSecure,
            token,
        )
    }

    static async put(url, data, isSecure = true, recaptchaToken = null) {
        let payload
        if (Array.isArray(data)) {
            // If data is an array, add LoginSource inside each object
            payload = data.map(item => ({
                ...item,
                LoginSource: 1,
            }))
        } else if (typeof data === 'object' && data !== null) {
            // If data is an object, add LoginSource at the root
            payload = { ...data, LoginSource: 1 }
        } else {
            // For other types, just send as is
            payload = data
        }
        const token = recaptchaToken ?? (await getRecaptchaToken('put'))
        return request(
            {
                url,
                method: 'PUT',
                data: payload,
            },
            isSecure,
            token,
        )
    }

    static moralisAPI(url) {
        const client = axios.create({
            baseURL: APIPath.moralisAPI,
        })
        return client({
            url,
            method: 'GET',
        })
            .then(onSuccess)
            .catch(onError)
    }

    static openseaAPI(url) {
        const headers = {}

        if (CommonConstant.mode === 'PRODUCTION') {
            headers['X-API-KEY'] = process.env.REACT_APP_OPENSEA_API_KEY
        }
        const client = axios.create({
            baseURL: APIPath.openSeaAPI,
            headers,
        })
        return client({
            url,
            method: 'GET',
        })
            .then(onSuccess)
            .catch(onError)
    }

    static extenralAPICall(url) {
        const client = axios({
            url,
            method: 'GET',
            timeout: 1000 * 3,
        })
        return client.then(onSuccess).catch(onError)
    }

    static remove(url, isSecure = true) {
        return request(
            {
                url,
                method: 'DELETE',
            },
            isSecure,
        )
    }

    static upload = async (url, data, header) => {
        const token = await getRecaptchaToken('upload')
        const hdrs = { ...(header || {}) }
        hdrs['X-Recaptcha'] = token || ''
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[reCAPTCHA] X-Recaptcha header token (upload):', token || '(empty)')
        }
        return uploadFiles(url, data, hdrs)
    }
}
